import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Mail, Lock, User as UserIcon, ArrowRight } from "lucide-react";

export default function MentorSignupPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirm) {
      setError("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data, error: signErr } = await supabase.auth.signUp({
        email: email.toLowerCase(),
        password,
        options: { data: { name, signup_intent: "mentor" } },
      });
      if (signErr) throw signErr;

      // Auto-login if signup didn't return a session.
      if (!data.session) {
        const { data: signIn, error: siErr } = await supabase.auth.signInWithPassword({
          email: email.toLowerCase(),
          password,
        });
        if (siErr) throw siErr;
        if (!signIn.session) {
          setError("Cadastro realizado. Confirme seu e-mail antes de continuar.");
          return;
        }
      }
      await refreshUser();
      // Persist mentor intent server-side.
      try {
        await api.post("/api/onboarding/mentor-intent", { signup_intent: "mentor" });
      } catch (intentErr) {
        // eslint-disable-next-line no-console
        console.warn("[mentor] mentor-intent post failed:", intentErr?.message);
      }
      // Beta mode: no Stripe. Directly provision the mentorship and land on /admin.
      try {
        await api.post("/api/onboarding/beta-activate");
      } catch (activateErr) {
        // eslint-disable-next-line no-console
        console.warn("[mentor] beta-activate failed:", activateErr?.message);
      }
      await refreshUser();
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err?.message || err?.response?.data?.detail || "Erro no cadastro.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="mentor-signup-container"
      className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FF6B00] to-[#E05000] flex items-center justify-center font-extrabold text-black text-xl mx-auto mb-3">
            M
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-white">Torne-se um Mentor</h1>
          <p className="text-xs text-gray-400 mt-1">
            Crie sua conta de mentor. Sua mentoria será criada automaticamente
            a partir do template Modo Caverna Standard e você poderá convidar
            alunos imediatamente.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#14161C] border border-[#282D3D] rounded-2xl p-5 shadow-2xl space-y-3"
        >
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Nome completo</label>
            <div className="relative">
              <UserIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="text"
                required
                data-testid="mentor-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">E-mail</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="email"
                required
                data-testid="mentor-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Senha</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="password"
                required
                minLength={6}
                data-testid="mentor-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Confirmar Senha</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="password"
                required
                minLength={6}
                data-testid="mentor-password-confirm-input"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className={`w-full bg-[#1A1D26] border rounded-lg py-2 pl-9 pr-3 text-white text-xs focus:outline-none ${
                  passwordConfirm && passwordConfirm !== password
                    ? "border-red-500 focus:border-red-500"
                    : "border-[#282D3D] focus:border-[#FF6B00]"
                }`}
              />
            </div>
            {passwordConfirm && passwordConfirm !== password && (
              <p className="text-[10px] text-red-400 mt-1">As senhas não coincidem.</p>
            )}
          </div>

          {error && (
            <div
              data-testid="mentor-signup-error"
              className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-[11px] text-red-300"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="mentor-signup-submit"
            className="w-full py-3 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <ShieldCheck className="w-4 h-4" />
            <span>{loading ? "Processando..." : "Criar conta de Mentor"}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <p className="text-[10px] text-center text-gray-500 mt-3">
          Sua mentoria será criada e você será redirecionado ao painel do mentor.
        </p>
      </div>
    </div>
  );
}
